'use client';

import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';

export type Role = 'admin' | 'trusted' | 'delegate' | 'viewer';

export interface AuthUser {
  id: string;
  name: string;
  role: Role;
}

export interface Permissions {
  canWrite: boolean;
  canEditTodos: boolean;
  canPurge: boolean;
  canReflect: boolean;
  canViewPersonal: boolean;
}

export interface InitialAuth {
  user: AuthUser | null;
  permissions: Permissions;
}

export interface AuthContextValue {
  user: AuthUser | null;
  role: Role;
  permissions: Permissions;
  isLoading: boolean;
  isAuthenticated: boolean;
  showSensitive: boolean;
  toggleShowSensitive: () => void;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const defaultPermissions: Permissions = {
  canWrite: false,
  canEditTodos: false,
  canPurge: false,
  canReflect: false,
  canViewPersonal: false,
};

const AuthContext = createContext<AuthContextValue>({
  user: null,
  role: 'viewer',
  permissions: defaultPermissions,
  isLoading: true,
  isAuthenticated: false,
  showSensitive: false,
  toggleShowSensitive: () => {},
  logout: async () => {},
  refresh: async () => {},
});

export function AuthProvider({ children, initialAuth }: { children: ReactNode; initialAuth?: InitialAuth }) {
  const [user, setUser] = useState<AuthUser | null>(initialAuth?.user ?? null);
  const [permissions, setPermissions] = useState<Permissions>(initialAuth?.permissions ?? defaultPermissions);
  const [isLoading, setIsLoading] = useState(!initialAuth);
  const [showSensitive, setShowSensitive] = useState(false);

  const fetchAuth = useCallback(async () => {
    try {
      const res = await fetch('/api/auth/me');
      const data = await res.json();

      if (data.user) {
        setUser(data.user);
      } else {
        setUser(null);
      }

      setPermissions(data.permissions || defaultPermissions);
    } catch (error) {
      console.error('Failed to fetch auth state:', error);
      setUser(null);
      setPermissions(defaultPermissions);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Check for token in URL on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');

    if (!token) return;

    const handleTokenLogin = async () => {
      try {
        const res = await fetch('/api/auth/login', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token }),
        });

        if (res.ok) {
          // Remove token from URL without reload
          const url = new URL(window.location.href);
          url.searchParams.delete('token');
          window.history.replaceState({}, '', url.toString());
        }
      } catch (error) {
        console.error('Token login failed:', error);
      }

      // Re-fetch auth state after token login
      await fetchAuth();
    };

    handleTokenLogin();
  }, [fetchAuth]);

  const logout = useCallback(async () => {
    try {
      await fetch('/api/auth/logout', { method: 'POST' });
      setUser(null);
      setPermissions(defaultPermissions);
    } catch (error) {
      console.error('Logout failed:', error);
    }
  }, []);

  const refresh = useCallback(async () => {
    setIsLoading(true);
    await fetchAuth();
  }, [fetchAuth]);

  const toggleShowSensitive = useCallback(() => {
    setShowSensitive(prev => !prev);
  }, []);

  const value: AuthContextValue = {
    user,
    role: user?.role || 'viewer',
    permissions,
    isLoading,
    isAuthenticated: !!user,
    showSensitive,
    toggleShowSensitive,
    logout,
    refresh,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
